/* ══════════════════════════════════════════════════════════════════
   Ein neuer Zeitraum schlägt einen neuen Betrag vor (F3, 04.10.2026;
   Befund AG-21). Ohne Abhängigkeiten, damit ein Test ihn ohne Browser prüft.

   Die API ändert beim Zeitraum Menge und Preis nicht selbst; sie schickt
   `vorschlag` mit (`dienste/zeitraumvorschlag.ts`). Die Maske fragt
   „übernehmen?", und übernommen wird über denselben Weg wie jede Änderung —
   `PATCH …/positionen/:pid` mit dem, was hier herauskommt.

   Seit dem 05.10.2026 (F3-Linie, „mach alles fertig") trägt der Vorschlag
   des Trägers die Vorschläge seiner Zubehörzeilen mit eigenem Preis
   (`anbauteile`). Ein Klick übernimmt alles.
   ══════════════════════════════════════════════════════════════════ */

type Zeilenvorschlag = {
  menge: number;
  einzelpreis: number;
  gesamt: number;
  einheit: string | null;
  menge_vorher: number;
  einzelpreis_vorher: number;
  gesamt_vorher: number;
  preis_von_hand: boolean;
  /**
   * Die Zeile ist angenommen (P4-01): Der Satz bleibt eingefroren, neu ist die
   * Dauer — für Miete und Zuschläge je Miettag zusammen.
   */
  eingefroren: boolean;
  miettage: number | null;
  miettage_vorher: number | null;
};

/** Der Vorschlag einer Zubehörzeile unter dem Träger. */
export type Anbauteilvorschlag = Zeilenvorschlag & { id: string; was: string };

export type Zeitraumvorschlag = Zeilenvorschlag & {
  /** Die mitgewanderten Zubehörzeilen mit eigenem Preis — leer, wenn keine. */
  anbauteile: Anbauteilvorschlag[];
};

const ZAHLFELDER = ["menge", "einzelpreis", "gesamt", "menge_vorher", "einzelpreis_vorher", "gesamt_vorher"];

function zeileAus(r: Record<string, unknown>): Zeilenvorschlag | null {
  const zahl = (k: string) => Number(r[k]);
  if (ZAHLFELDER.some((k) => !Number.isFinite(zahl(k)))) return null;
  const tage = (k: string) => (r[k] !== null && r[k] !== undefined && Number.isFinite(zahl(k)) ? zahl(k) : null);
  return {
    menge: zahl("menge"),
    einzelpreis: zahl("einzelpreis"),
    gesamt: zahl("gesamt"),
    einheit: typeof r["einheit"] === "string" ? r["einheit"] : null,
    menge_vorher: zahl("menge_vorher"),
    einzelpreis_vorher: zahl("einzelpreis_vorher"),
    gesamt_vorher: zahl("gesamt_vorher"),
    preis_von_hand: r["preis_von_hand"] === true,
    eingefroren: r["eingefroren"] === true,
    miettage: tage("miettage"),
    miettage_vorher: tage("miettage_vorher"),
  };
}

/** Den Vorschlag aus der Antwort lesen — `null`, wenn keiner oder keiner lesbar. */
export function vorschlagAus(roh: unknown): Zeitraumvorschlag | null {
  if (!roh || typeof roh !== "object") return null;
  const v = (roh as { vorschlag?: unknown }).vorschlag;
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  const traeger = zeileAus(r);
  if (!traeger) return null;
  const anbauteile: Anbauteilvorschlag[] = [];
  for (const a of Array.isArray(r["anbauteile"]) ? r["anbauteile"] : []) {
    if (!a || typeof a !== "object") continue;
    const ar = a as Record<string, unknown>;
    const zeile = zeileAus(ar);
    if (!zeile || typeof ar["id"] !== "string") continue;
    anbauteile.push({ ...zeile, id: ar["id"], was: typeof ar["was"] === "string" ? ar["was"] : "" });
  }
  return { ...traeger, anbauteile };
}

/** Summe vorher und vorgeschlagen — Träger und Anbauteile zusammen, auf den Cent. */
export function vorschlagSumme(v: Zeitraumvorschlag): { vorher: number; neu: number } {
  const runden = (x: number) => Math.round(x * 100) / 100;
  return {
    vorher: runden(v.anbauteile.reduce((s, a) => s + a.gesamt_vorher, v.gesamt_vorher)),
    neu: runden(v.anbauteile.reduce((s, a) => s + a.gesamt, v.gesamt)),
  };
}

type Zeilenrumpf = { menge: number; einzelpreis?: number; dauer_uebernehmen?: true };

/**
 * Was „Übernehmen" für eine Zeile schickt. Ein Preis von Hand bleibt (Lucas
 * Entscheidung): dann geht nur die Menge hinaus. An einer angenommenen Zeile
 * ist der Satz eingefroren (Regel 4) — dort geht die Menge mit
 * `dauer_uebernehmen` hinaus, und die API schreibt die neue Dauer in den
 * Schnappschuss, damit die Zuschläge ihr folgen (P4-01).
 */
function zeilenrumpf(v: Zeilenvorschlag): Zeilenrumpf {
  if (v.eingefroren) return { menge: v.menge, dauer_uebernehmen: true };
  return v.preis_von_hand ? { menge: v.menge } : { menge: v.menge, einzelpreis: v.einzelpreis };
}

/** Was „Übernehmen" schickt — der Träger und, wo vorhanden, seine Anbauteile im selben Rumpf. */
export function vorschlagRumpf(
  v: Zeitraumvorschlag,
): Zeilenrumpf & { anbauteile?: (Zeilenrumpf & { id: string })[] } {
  const traeger = zeilenrumpf(v);
  if (!v.anbauteile.length) return traeger;
  return { ...traeger, anbauteile: v.anbauteile.map((a) => ({ id: a.id, ...zeilenrumpf(a) })) };
}
